import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Star, StarOff, Eye, EyeOff, GripVertical, RotateCcw, Loader2 } from 'lucide-react';
import { navPrefsApi } from '../../lib/api';
import { toast } from 'sonner';

/**
 * Customize-sidebar dialog.
 *
 * Props:
 *   open:           boolean
 *   onOpenChange:   (bool) => void
 *   baseItems:      the role's full navigation array [{ name, path, icon }, ...]
 *   initial:        { order: [paths], favorites: [paths], hidden: [paths] }
 *   onSaved:        (newPrefs) => void  — called after successful save so the
 *                   parent can apply the new ordering without a full reload.
 *
 * Uses native HTML5 drag-and-drop so we don't pull in another dependency.
 */
export default function CustomizeMenuDialog({ open, onOpenChange, baseItems, initial, onSaved }) {
  const [order, setOrder] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [saving, setSaving] = useState(false);
  const dragPathRef = useRef(null);

  // Whenever the dialog opens, seed the working state from the props.
  useEffect(() => {
    if (!open) return;
    const basePaths = baseItems.map(i => i.path);
    // `order` is the user's explicit order; anything missing is appended in
    // the default role-order so new role features become visible without a
    // manual resort.
    const savedOrder = (initial?.order || []).filter(p => basePaths.includes(p));
    const remaining = basePaths.filter(p => !savedOrder.includes(p));
    setOrder([...savedOrder, ...remaining]);
    setFavorites((initial?.favorites || []).filter(p => basePaths.includes(p)));
    setHidden((initial?.hidden || []).filter(p => basePaths.includes(p)));
  }, [open, initial, baseItems]);

  const itemsByPath = Object.fromEntries(baseItems.map(i => [i.path, i]));

  const onDragStart = (path) => { dragPathRef.current = path; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (targetPath) => {
    const src = dragPathRef.current;
    dragPathRef.current = null;
    if (!src || src === targetPath) return;
    setOrder(prev => {
      const next = [...prev];
      const srcIdx = next.indexOf(src);
      const tgtIdx = next.indexOf(targetPath);
      if (srcIdx < 0 || tgtIdx < 0) return prev;
      next.splice(srcIdx, 1);
      next.splice(tgtIdx, 0, src);
      return next;
    });
  };

  const toggleFavorite = (path) => {
    setFavorites(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };
  const toggleHidden = (path) => {
    setHidden(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
    // A hidden item can't also be favourited.
    setFavorites(prev => prev.filter(p => p !== path || !hidden.includes(path)));
  };

  const resetAll = () => {
    setOrder(baseItems.map(i => i.path));
    setFavorites([]);
    setHidden([]);
  };

  const save = async () => {
    try {
      setSaving(true);
      const payload = { order, favorites, hidden };
      const { data } = await navPrefsApi.update(payload);
      toast.success('Sidebar preferences saved');
      onSaved?.(data.nav_prefs || payload);
      onOpenChange(false);
    } catch {
      toast.error('Could not save preferences');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="customize-menu-dialog">
        <DialogHeader>
          <DialogTitle>Customize Menu</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-[#64748B]">
            Drag items to reorder, star to pin as a favorite, or hide items you don't use.
          </p>
          <div className="max-h-[420px] overflow-y-auto border rounded-lg divide-y divide-[#F1F5F9]" data-testid="customize-menu-list">
            {order.map((path) => {
              const item = itemsByPath[path];
              if (!item) return null;
              const isFav = favorites.includes(path);
              const isHidden = hidden.includes(path);
              const Icon = item.icon;
              return (
                <div
                  key={path}
                  draggable
                  onDragStart={() => onDragStart(path)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(path)}
                  className={`flex items-center gap-2 px-3 py-2 cursor-move hover:bg-[#F8FAFC] ${isHidden ? 'opacity-50' : ''}`}
                  data-testid={`customize-row-${path}`}
                >
                  <GripVertical className="w-4 h-4 text-[#94A3B8]" />
                  {Icon && <Icon className="w-4 h-4 text-[#64748B]" />}
                  <span className="flex-1 text-sm text-[#0A2342]">{item.name}</span>
                  <button
                    type="button"
                    onClick={() => toggleFavorite(path)}
                    disabled={isHidden}
                    className={`w-7 h-7 rounded flex items-center justify-center ${isFav ? 'text-[#F59E0B]' : 'text-[#94A3B8] hover:text-[#F59E0B]'} disabled:opacity-40`}
                    title={isFav ? 'Remove from favorites' : 'Add to favorites'}
                    data-testid={`customize-fav-${path}`}
                  >
                    {isFav ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleHidden(path)}
                    className={`w-7 h-7 rounded flex items-center justify-center ${isHidden ? 'text-[#991B1B]' : 'text-[#94A3B8] hover:text-[#991B1B]'}`}
                    title={isHidden ? 'Show in menu' : 'Hide from menu'}
                    data-testid={`customize-hide-${path}`}
                  >
                    {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" size="sm" onClick={resetAll} data-testid="customize-reset">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset to default
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="customize-cancel">Cancel</Button>
              <Button className="careiq-btn-primary" onClick={save} disabled={saving} data-testid="customize-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
